import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Pause, Minimize2, Maximize2, X, ChevronLeft } from 'lucide-react';

// LIVE-CONSUMER-PRODUCT-VIDEO-1 · 소비자 개인 재생 플로팅 플레이어 (LIVE 트랙과 무관 · 같은 <video> 유지로 크기 변경 시 위치 유지).
const W = { md: [170, 200, 0.46], sm: [105, 125, 0.3] };
const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const mmss = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}` : '0:00');

const widthOf = (size, vw) => {
  const [min, max, frac] = W[size];
  return Math.round(Math.min(max, Math.max(min, vw * frac)));
};

const useBoxDrag = (boundsRef, size) => {
  const [pos, setPos] = useState(null);
  const drag = useRef(null);
  const clamp = useCallback((p, w) => {
    const b = boundsRef.current?.getBoundingClientRect();
    if (!b) return p;
    const h = Math.round((w * 16) / 9) + 28;
    return { x: Math.min(Math.max(4, p.x), Math.max(4, b.width - w - 4)),
      y: Math.min(Math.max(48, p.y), Math.max(48, b.height - h - 8)) };
  }, [boundsRef]);
  useEffect(() => {
    if (size === 'full') return undefined;
    const fit = () => {
      const b = boundsRef.current?.getBoundingClientRect();
      if (!b) return;
      const w = widthOf(size, b.width);
      setPos((p) => clamp(p || { x: (b.width - w) / 2, y: b.height * 0.14 }, w));
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    return () => { window.removeEventListener('resize', fit); window.removeEventListener('orientationchange', fit); };
  }, [size, boundsRef, clamp]);
  const onPointerDown = (e) => {
    if (size === 'full' || e.target.closest('button')) return;
    drag.current = { sx: e.clientX, sy: e.clientY, p: pos };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const w = widthOf(size, boundsRef.current?.getBoundingClientRect().width || 390);
    setPos(clamp({ x: drag.current.p.x + e.clientX - drag.current.sx, y: drag.current.p.y + e.clientY - drag.current.sy }, w));
  };
  const onPointerUp = () => { drag.current = null; };
  return { pos, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } };
};

const Controls = ({ playing, t, dur, onToggle, onSeek, compact }) => (
  <div className="flex items-center gap-1 px-1.5 py-1 bg-black/70 text-white min-w-0" data-testid="pv-controls">
    <button type="button" onClick={onToggle} className="p-1" aria-label={playing ? '일시정지' : '재생'} data-testid="pv-play-toggle">
      {playing ? <Pause size={14} /> : <Play size={14} />}
    </button>
    {!compact && (
      <>
        <input type="range" min={0} max={dur || 0} step={0.1} value={Math.min(t, dur || 0)} onChange={(e) => onSeek(Number(e.target.value))}
               className="flex-1 min-w-0 h-1 accent-[#FF3B30]" aria-label="재생 위치" data-testid="pv-progress" />
        <span className="text-[9px] tabular-nums shrink-0" data-testid="pv-time">{mmss(t)} / {mmss(dur)}</span>
      </>
    )}
  </div>
);

const Header = ({ size, setSize, prevSize, onClose, handlers }) => (
  <div className={`flex items-center gap-1 px-1.5 h-7 bg-black/80 text-white select-none ${size === 'full' ? '' : 'cursor-move touch-none'}`}
       {...handlers} data-testid="pv-header">
    {size === 'full' ? (
      <button type="button" onClick={() => setSize(prevSize)} className="flex items-center gap-0.5 text-[11px] font-bold p-1" data-testid="pv-exit-full">
        <ChevronLeft size={14} /> 작은 화면으로
      </button>
    ) : (
      <span className="text-[10px] font-bold truncate flex-1" data-testid="pv-title">{size === 'sm' ? '소개영상' : '상품 소개영상'}</span>
    )}
    {size === 'full' && <span className="flex-1" />}
    {size === 'md' && <button type="button" onClick={() => setSize('sm')} className="p-1" aria-label="축소" data-testid="pv-shrink"><Minimize2 size={12} /></button>}
    {size !== 'full' && <button type="button" onClick={() => setSize('full')} className="p-1" aria-label="전체화면" data-testid="pv-expand"><Maximize2 size={12} /></button>}
    {size === 'sm' && <button type="button" onClick={() => setSize('md')} className="p-1 text-[10px] font-bold" aria-label="중간 크기" data-testid="pv-medium">M</button>}
    <button type="button" onClick={onClose} className="p-1" aria-label="닫기" data-testid="pv-close"><X size={13} /></button>
  </div>
);

export const ProductVideoPlayer = ({ item, boundsRef, onClose, onBuy }) => {
  const vref = useRef(null);
  const [size, setSizeRaw] = useState('md');
  const prev = useRef('md');
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [err, setErr] = useState(null);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(item.duration_sec || 0);
  const { pos, handlers } = useBoxDrag(boundsRef, size);
  const setSize = (s) => { if (size !== 'full') prev.current = size; setSizeRaw(s); };

  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    v.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
  }, [item.url]);

  const toggle = () => {
    const v = vref.current;
    if (!v) return;
    if (v.paused) v.play().then(() => setBlocked(false)).catch(() => setBlocked(true)); else v.pause();
  };
  const full = size === 'full';
  const w = full ? null : widthOf(size, boundsRef.current?.getBoundingClientRect().width || 390);
  const style = full ? undefined : { left: pos?.x ?? 0, top: pos?.y ?? 0, width: w, visibility: pos ? 'visible' : 'hidden' };
  const p = item.product || {};
  return (
    <div className={full ? 'fixed inset-0 z-[70] bg-black flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]'
      : 'absolute z-40 rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/20 bg-black'}
         style={style} onClick={(e) => e.stopPropagation()} data-testid="product-video-player" data-size={size}
         data-product-id={p.product_id}>
      <Header size={size} setSize={setSize} prevSize={prev.current} onClose={onClose} handlers={handlers} />
      <div className={`relative bg-black ${full ? 'flex-1 min-h-0 flex items-center justify-center' : ''}`}>
        <video ref={vref} src={item.url} playsInline preload="auto"
               className={full ? 'max-h-full max-w-full aspect-[9/16] h-full object-contain' : 'w-full aspect-[9/16] object-cover'}
               onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
               onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
               onLoadedMetadata={(e) => setDur(e.currentTarget.duration || dur)}
               onEnded={onClose} onError={() => setErr('영상을 재생할 수 없어요. 잠시 후 다시 시도해 주세요.')}
               onClick={toggle} data-testid="pv-video" />
        {blocked && !err && (
          <button type="button" onClick={toggle} className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/50 text-white text-[11px] font-bold" data-testid="pv-tap-to-play">
            <Play size={22} /> 탭하여 재생
          </button>
        )}
        {err && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 text-white text-[11px] text-center px-2" data-testid="pv-error">
            {err}
            <button type="button" onClick={onClose} className="px-3 py-1 rounded-full bg-white text-black font-bold" data-testid="pv-error-close">닫기</button>
          </div>
        )}
      </div>
      <Controls playing={playing} t={t} dur={dur} onToggle={toggle} onSeek={(s) => { if (vref.current) vref.current.currentTime = s; }} compact={size === 'sm'} />
      {full && (
        <div className="flex items-center gap-3 px-4 py-3 bg-white text-gray-900" data-testid="pv-full-product">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black line-clamp-1" data-testid="pv-product-name">{p.name}</p>
            <p className="text-sm font-black" data-testid="pv-product-price">{won(p.sale_price)}</p>
          </div>
          <button type="button" onClick={() => { setSize(prev.current); onBuy(p); }} className="shrink-0 px-5 py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-black" data-testid="pv-buy">
            구매하기
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductVideoPlayer;
